import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getReleves, getReleve, createReleve, updateReleve, deleteReleve,
  soumettreReleve, validerReleve, rejeterReleve,
  getStations, getPompes, getProduits, getRhPompistes
} from '../services/api';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';
const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];

const Releves: React.FC = () => {
  const { user } = useAuth();
  const [releves, setReleves] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [stationId, setStationId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Detail modal
  const [selectedReleve, setSelectedReleve] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create/Edit form
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [pompes, setPompes] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [pompistes, setPompistes] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    pompe_id: '', produit_id: '', pompiste_id: '', date_releve: new Date().toISOString().split('T')[0],
    poste: 'matin', index_debut: '', index_fin: '',
    montant_especes: '', montant_electronique: ''
  });
  const [saving, setSaving] = useState(false);

  // Soumettre modal
  const [showSoumettre, setShowSoumettre] = useState(false);
  const [soumettreData, setSoumettreData] = useState({ montant_especes: '', montant_electronique: '' });

  // Rejeter modal
  const [showRejeter, setShowRejeter] = useState(false);
  const [rejetCommentaire, setRejetCommentaire] = useState('');

  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');
  const isGerant = user?.role === 'gerant';

  useEffect(() => {
    if (isGlobal) {
      getStations().then(r => setStations(r.data)).catch(console.error);
    }
    getProduits().then(r => setProduits(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    loadReleves();
  }, [stationId, date]);

  const loadReleves = async () => {
    setLoading(true);
    try {
      const params: any = { limit: '100' };
      if (stationId) params.station_id = stationId;
      if (date) params.date = date;
      const res = await getReleves(params);
      setReleves(res.data.data);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── Ouvrir le detail ──
  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const res = await getReleve(id);
      setSelectedReleve(res.data);
    } catch (err) { console.error(err); }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setSelectedReleve(null);
    setShowSoumettre(false);
    setShowRejeter(false);
  };

  // ── Ouvrir le formulaire (creation/edition) ──
  const openForm = async (releve?: any) => {
    // Charger les pompes et pompistes de la station
    const sid = isGerant ? user?.station_id : (stationId || undefined);
    if (sid) {
      try {
        const [pompesRes, pompistesRes] = await Promise.all([
          getPompes({ station_id: String(sid) }),
          getRhPompistes({ station_id: String(sid) })
        ]);
        setPompes(pompesRes.data);
        setPompistes(pompistesRes.data);
      } catch (err) { console.error(err); }
    }

    if (releve) {
      setEditMode(true);
      setEditId(releve.id);
      setFormData({
        pompe_id: String(releve.pompe_id),
        produit_id: String(releve.produit_id),
        pompiste_id: String(releve.pompiste_id || ''),
        date_releve: releve.date_releve?.split('T')[0] || date,
        poste: releve.poste,
        index_debut: String(releve.index_debut),
        index_fin: String(releve.index_fin),
        montant_especes: String(releve.montant_especes || 0),
        montant_electronique: String(releve.montant_electronique || 0)
      });
    } else {
      setEditMode(false);
      setEditId(null);
      setFormData({
        pompe_id: '', produit_id: '', pompiste_id: '', date_releve: date,
        poste: 'matin', index_debut: '', index_fin: '',
        montant_especes: '0', montant_electronique: '0'
      });
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditMode(false);
    setEditId(null);
  };

  // ── Quand on selectionne une pompe, auto-remplir le produit ──
  const handlePompeChange = (pompeId: string) => {
    setFormData(prev => ({ ...prev, pompe_id: pompeId }));
    const pompe = pompes.find((p: any) => String(p.id) === pompeId);
    if (pompe) {
      setFormData(prev => ({
        ...prev,
        pompe_id: pompeId,
        produit_id: String(pompe.produit_id),
        index_debut: String(pompe.dernier_index || 0)
      }));
    }
  };

  // ── Sauvegarder (creer ou modifier) ──
  const handleSave = async () => {
    if (!formData.pompe_id || !formData.index_debut || !formData.index_fin) {
      return alert('Veuillez remplir tous les champs obligatoires');
    }
    if (Number(formData.index_fin) <= Number(formData.index_debut)) {
      return alert('L\'index de fin doit etre superieur a l\'index de debut');
    }
    setSaving(true);
    try {
      const sid = isGerant ? user?.station_id : (stationId ? Number(stationId) : null);
      const data: any = {
        station_id: sid,
        pompe_id: Number(formData.pompe_id),
        produit_id: Number(formData.produit_id),
        date_releve: formData.date_releve,
        poste: formData.poste,
        index_debut: Number(formData.index_debut),
        index_fin: Number(formData.index_fin),
        montant_especes: Number(formData.montant_especes) || 0,
        montant_electronique: Number(formData.montant_electronique) || 0
      };
      if (formData.pompiste_id) data.pompiste_id = Number(formData.pompiste_id);

      if (editMode && editId) {
        await updateReleve(editId, data);
      } else {
        await createReleve(data);
      }
      closeForm();
      loadReleves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  // ── Supprimer ──
  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce releve ? Cette action est irreversible.')) return;
    try {
      await deleteReleve(id);
      closeDetail();
      loadReleves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ── Soumettre ──
  const handleSoumettre = async () => {
    if (!selectedReleve) return;
    setSaving(true);
    try {
      await soumettreReleve(selectedReleve.id, {
        montant_especes: Number(soumettreData.montant_especes) || 0,
        montant_electronique: Number(soumettreData.montant_electronique) || 0
      });
      setShowSoumettre(false);
      const res = await getReleve(selectedReleve.id);
      setSelectedReleve(res.data);
      loadReleves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  // ── Valider ──
  const handleValider = async () => {
    if (!selectedReleve) return;
    if (!window.confirm('Valider ce releve ? L\'index de la pompe sera mis a jour.')) return;
    try {
      await validerReleve(selectedReleve.id);
      const res = await getReleve(selectedReleve.id);
      setSelectedReleve(res.data);
      loadReleves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // ── Rejeter ──
  const handleRejeter = async () => {
    if (!selectedReleve || !rejetCommentaire.trim()) return alert('Veuillez indiquer un motif de rejet');
    setSaving(true);
    try {
      await rejeterReleve(selectedReleve.id, { commentaire: rejetCommentaire });
      setShowRejeter(false);
      setRejetCommentaire('');
      const res = await getReleve(selectedReleve.id);
      setSelectedReleve(res.data);
      loadReleves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  // ── Helpers ──
  const canModify = (r: any) => ['brouillon', 'rejete'].includes(r?.statut);
  const canSoumettre = (r: any) => r?.statut === 'brouillon';
  const canValider = (r: any) => r?.statut === 'soumis' && (isGlobal || isGerant);
  const canRejeter = (r: any) => r?.statut === 'soumis' && (isGlobal || isGerant);

  const volumeCalc = Number(formData.index_fin) - Number(formData.index_debut);
  const selectedProduit = produits.find((p: any) => String(p.id) === formData.produit_id);
  const montantTheoCalc = volumeCalc > 0 ? volumeCalc * (selectedProduit?.prix_vente || 0) : 0;

  return (
    <div>
      <div className="page-header">
        <h2>Releves Pompes</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="badge">{total} releves</span>
          {(isGerant || isGlobal) && (
            <button className="btn btn-primary" onClick={() => openForm()}>
              + Nouveau Releve
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        {isGlobal && (
          <select value={stationId} onChange={e => setStationId(e.target.value)}>
            <option value="">Toutes les stations</option>
            {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th><th>Poste</th><th>Pompe</th><th>Produit</th><th>Pompiste</th>
                <th style={{textAlign:'right'}}>Idx Debut</th><th style={{textAlign:'right'}}>Idx Fin</th>
                <th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>Theorique</th>
                <th style={{textAlign:'right'}}>Encaisse</th><th style={{textAlign:'right'}}>Ecart</th>
                <th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releves.length === 0 ? (
                <tr><td colSpan={13} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun releve pour cette date</td></tr>
              ) : releves.map((r: any) => (
                <tr key={r.id}>
                  <td>{new Date(r.date_releve).toLocaleDateString('fr-FR')}</td>
                  <td><span className={`badge badge-${r.poste === 'matin' ? 'info' : 'warning'}`}>{r.poste}</span></td>
                  <td><code>{r.pompe_code}</code></td>
                  <td>{r.produit_nom}</td>
                  <td>{r.pompiste_prenom} {r.pompiste_nom}</td>
                  <td style={{textAlign:'right'}}>{Number(r.index_debut).toLocaleString()}</td>
                  <td style={{textAlign:'right'}}>{Number(r.index_fin).toLocaleString()}</td>
                  <td style={{textAlign:'right'}}><strong>{Number(r.volume_vendu).toLocaleString()} L</strong></td>
                  <td style={{textAlign:'right'}}>{formatMontant(r.montant_theorique)}</td>
                  <td style={{textAlign:'right'}}>{formatMontant(r.montant_encaisse)}</td>
                  <td style={{textAlign:'right', color: Math.abs(r.ecart) > 1000 ? '#e53935' : '#4caf50'}}>
                    {r.ecart > 0 ? '+' : ''}{formatMontant(r.ecart)}
                  </td>
                  <td><span className={`badge badge-${r.statut === 'valide' ? 'success' : r.statut === 'rejete' ? 'danger' : r.statut === 'soumis' ? 'info' : 'warning'}`}>{r.statut}</span></td>
                  <td>
                    <button className="btn btn-sm" onClick={() => openDetail(r.id)} title="Voir details">
                      {canModify(r) ? '✏️' : '👁'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ══════ MODAL: Detail du Releve ══════ */}
      {showDetail && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                Releve {selectedReleve ? `du ${new Date(selectedReleve.date_releve).toLocaleDateString('fr-FR')}` : '...'}
                {selectedReleve && (
                  <span className={`badge badge-${selectedReleve.statut === 'valide' ? 'success' : selectedReleve.statut === 'rejete' ? 'danger' : selectedReleve.statut === 'soumis' ? 'info' : 'warning'}`} style={{ marginLeft: 12 }}>
                    {selectedReleve.statut}
                  </span>
                )}
              </h3>
              <button className="modal-close" onClick={closeDetail}>&times;</button>
            </div>
            <div className="modal-body">
              {detailLoading ? <div className="loading">Chargement...</div> : selectedReleve && (
                <>
                  {/* Infos principales */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                    <div className="recap-card">
                      <span className="recap-label">Station</span>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedReleve.station_nom}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Pompe / Produit</span>
                      <span style={{ fontWeight: 700, fontSize: 14 }}><code>{selectedReleve.pompe_code}</code> - {selectedReleve.produit_nom}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Poste</span>
                      <span className={`badge badge-${selectedReleve.poste === 'matin' ? 'info' : 'warning'}`}>{selectedReleve.poste}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Pompiste</span>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedReleve.pompiste_prenom} {selectedReleve.pompiste_nom}</span>
                    </div>
                  </div>

                  {/* Index et volumes */}
                  <div className="journal-recap">
                    <div className="recap-card">
                      <span className="recap-label">Index Debut</span>
                      <span className="recap-value">{Number(selectedReleve.index_debut).toLocaleString()}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Index Fin</span>
                      <span className="recap-value">{Number(selectedReleve.index_fin).toLocaleString()}</span>
                    </div>
                    <div className="recap-card recap-green">
                      <span className="recap-label">Volume Vendu</span>
                      <span className="recap-value">{Number(selectedReleve.volume_vendu).toLocaleString()} L</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Prix Unitaire</span>
                      <span className="recap-value">{formatMontant(selectedReleve.prix_unitaire)}</span>
                    </div>
                  </div>

                  {/* Montants */}
                  <div className="journal-recap" style={{ marginTop: 16 }}>
                    <div className="recap-card">
                      <span className="recap-label">Montant Theorique</span>
                      <span className="recap-value">{formatMontant(selectedReleve.montant_theorique)}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Especes</span>
                      <span className="recap-value">{formatMontant(selectedReleve.montant_especes)}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Electronique</span>
                      <span className="recap-value">{formatMontant(selectedReleve.montant_electronique)}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Total Encaisse</span>
                      <span className="recap-value">{formatMontant(selectedReleve.montant_encaisse)}</span>
                    </div>
                    <div className={`recap-card ${Math.abs(selectedReleve.ecart) > 1000 ? 'recap-red' : 'recap-green'}`}>
                      <span className="recap-label">Ecart</span>
                      <span className="recap-value">
                        {selectedReleve.ecart > 0 ? '+' : ''}{formatMontant(selectedReleve.ecart)}
                        <small style={{ display: 'block', fontSize: 12 }}>({selectedReleve.ecart_pourcent}%)</small>
                      </span>
                    </div>
                  </div>

                  {/* Commentaire de rejet */}
                  {selectedReleve.statut === 'rejete' && selectedReleve.commentaire && (
                    <div style={{ marginTop: 16, padding: '12px 16px', background: '#ffebee', borderRadius: 8, borderLeft: '4px solid #e53935' }}>
                      <strong style={{ color: '#e53935' }}>Motif du rejet :</strong>
                      <p style={{ margin: '4px 0 0', color: '#c62828' }}>{selectedReleve.commentaire}</p>
                    </div>
                  )}

                  {/* Validation info */}
                  {selectedReleve.statut === 'valide' && selectedReleve.valideur_nom && (
                    <div style={{ marginTop: 16, padding: '12px 16px', background: '#e8f5e9', borderRadius: 8, borderLeft: '4px solid #4caf50' }}>
                      <strong style={{ color: '#2e7d32' }}>Valide par :</strong> {selectedReleve.valideur_prenom} {selectedReleve.valideur_nom}
                      {selectedReleve.date_validation && (
                        <span style={{ marginLeft: 12, fontSize: 13, color: '#666' }}>
                          le {new Date(selectedReleve.date_validation).toLocaleString('fr-FR')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── Formulaire Soumettre ── */}
                  {showSoumettre && (
                    <div style={{ marginTop: 20, padding: 20, background: '#f5f5f5', borderRadius: 8 }}>
                      <h4 style={{ marginBottom: 12 }}>Saisir les montants encaisses</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Montant especes (FCFA)</label>
                          <input type="number" value={soumettreData.montant_especes}
                            onChange={e => setSoumettreData(p => ({ ...p, montant_especes: e.target.value }))}
                            placeholder="0" />
                        </div>
                        <div className="form-group">
                          <label>Montant electronique (FCFA)</label>
                          <input type="number" value={soumettreData.montant_electronique}
                            onChange={e => setSoumettreData(p => ({ ...p, montant_electronique: e.target.value }))}
                            placeholder="0" />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button className="btn" onClick={() => setShowSoumettre(false)}>Annuler</button>
                        <button className="btn btn-primary" onClick={handleSoumettre} disabled={saving}>
                          {saving ? 'Envoi...' : 'Soumettre le releve'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Formulaire Rejeter ── */}
                  {showRejeter && (
                    <div style={{ marginTop: 20, padding: 20, background: '#fff3e0', borderRadius: 8 }}>
                      <h4 style={{ marginBottom: 12 }}>Motif du rejet</h4>
                      <textarea value={rejetCommentaire} onChange={e => setRejetCommentaire(e.target.value)}
                        placeholder="Indiquez la raison du rejet..." rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ddd' }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button className="btn" onClick={() => setShowRejeter(false)}>Annuler</button>
                        <button className="btn btn-danger" onClick={handleRejeter} disabled={saving}>
                          {saving ? 'Rejet...' : 'Confirmer le rejet'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer actions */}
            {selectedReleve && !showSoumettre && !showRejeter && (
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {canModify(selectedReleve) && (
                    <>
                      <button className="btn btn-warning" onClick={() => { closeDetail(); openForm(selectedReleve); }}>
                        ✏️ Modifier
                      </button>
                      <button className="btn btn-danger" onClick={() => handleDelete(selectedReleve.id)}>
                        🗑️ Supprimer
                      </button>
                    </>
                  )}
                  {canSoumettre(selectedReleve) && (
                    <button className="btn btn-primary" onClick={() => {
                      setSoumettreData({
                        montant_especes: String(selectedReleve.montant_especes || 0),
                        montant_electronique: String(selectedReleve.montant_electronique || 0)
                      });
                      setShowSoumettre(true);
                    }}>
                      📤 Soumettre
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {canValider(selectedReleve) && (
                    <button className="btn btn-success" onClick={handleValider}>
                      ✅ Valider
                    </button>
                  )}
                  {canRejeter(selectedReleve) && (
                    <button className="btn btn-danger" onClick={() => setShowRejeter(true)}>
                      ❌ Rejeter
                    </button>
                  )}
                  <button className="btn" onClick={closeDetail}>Fermer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════ MODAL: Formulaire Creation/Edition ══════ */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Modifier le releve' : 'Nouveau releve de pompe'}</h3>
              <button className="modal-close" onClick={closeForm}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Station (admin only) */}
              {isGlobal && (
                <div className="form-group">
                  <label>Station *</label>
                  <select value={stationId} onChange={async e => {
                    setStationId(e.target.value);
                    if (e.target.value) {
                      const [pompesRes, pompistesRes] = await Promise.all([
                        getPompes({ station_id: e.target.value }),
                        getRhPompistes({ station_id: e.target.value })
                      ]);
                      setPompes(pompesRes.data);
                      setPompistes(pompistesRes.data);
                    }
                  }}>
                    <option value="">-- Selectionner --</option>
                    {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom} - {s.ville}</option>)}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Pompe *</label>
                  <select value={formData.pompe_id} onChange={e => handlePompeChange(e.target.value)}>
                    <option value="">-- Selectionner une pompe --</option>
                    {pompes.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.code} - {p.produit_nom || 'N/A'} (idx: {Number(p.dernier_index || 0).toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Produit *</label>
                  <select value={formData.produit_id} onChange={e => setFormData(p => ({ ...p, produit_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {produits.filter((p: any) => p.categorie === 'carburant').map((p: any) => (
                      <option key={p.id} value={p.id}>{p.nom} ({formatMontant(p.prix_vente)}/L)</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={formData.date_releve}
                    onChange={e => setFormData(p => ({ ...p, date_releve: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Poste *</label>
                  <select value={formData.poste} onChange={e => setFormData(p => ({ ...p, poste: e.target.value }))}>
                    <option value="matin">Matin</option>
                    <option value="soir">Soir</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Pompiste *</label>
                <select value={formData.pompiste_id} onChange={e => setFormData(p => ({ ...p, pompiste_id: e.target.value }))}>
                  <option value="">-- Selectionner le pompiste --</option>
                  {pompistes.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Index Debut *</label>
                  <input type="number" value={formData.index_debut} placeholder="Ex: 125430"
                    onChange={e => setFormData(p => ({ ...p, index_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Index Fin *</label>
                  <input type="number" value={formData.index_fin} placeholder="Ex: 125680"
                    onChange={e => setFormData(p => ({ ...p, index_fin: e.target.value }))} />
                </div>
              </div>

              {/* Calculs automatiques */}
              {volumeCalc > 0 && (
                <div className="journal-recap" style={{ marginTop: 12 }}>
                  <div className="recap-card recap-green">
                    <span className="recap-label">Volume</span>
                    <span className="recap-value">{volumeCalc.toLocaleString()} L</span>
                  </div>
                  <div className="recap-card">
                    <span className="recap-label">Montant Theorique</span>
                    <span className="recap-value">{formatMontant(montantTheoCalc)}</span>
                  </div>
                </div>
              )}

              <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #eee' }} />
              <h4 style={{ marginBottom: 12, color: '#555' }}>Montants encaisses (optionnel)</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Especes (FCFA)</label>
                  <input type="number" value={formData.montant_especes} placeholder="0"
                    onChange={e => setFormData(p => ({ ...p, montant_especes: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Paiement electronique (FCFA)</label>
                  <input type="number" value={formData.montant_electronique} placeholder="0"
                    onChange={e => setFormData(p => ({ ...p, montant_electronique: e.target.value }))} />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn" onClick={closeForm}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : (editMode ? 'Enregistrer les modifications' : 'Creer le releve')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Releves;
